import React, { useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Component imports
import LogInForm from './auth/LoginForm';
import RegisterForm from './auth/RegisterForm';
import ForgotForm from './auth/ForgotForm';
import TwoMFA from './auth/TwoMFA';
import Dashboard from './app/dashboard/Dashboard';
import ThemeReinit from '@/components/ThemeReinit';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';

import Profile from './app/profile/Profile';
import LibraryAdmin from './app/admin/library/LibraryAdmin';
import DirectoryAdmin from './app/admin/directory/DirectoryAdmin';
import UserAdmin from './app/admin/user/UserAdmin';
import ArticlesAdmin from './app/admin/articles/ArticlesAdmin';
import Library from './app/library/Library';
import Articles from './app/articles/Articles';
import Memorial from './app/memorial/Memorial';
import Dog from './app/dog/Dog';
import Directory from './app/directory/Directory';
import Calendar from './app/calendar/Calendar';
import Question from './app/question/Question';
import Error404 from '@components/Error404';
import MarketplaceAdmin from './app/admin/marketplace/MarketplaceAdmin';
import Products from './app/vendor/products/Products';
import Adverts from './app/vendor/adverts/Adverts';
import Marketplace from './app/marketplace/Marketplace';
import MarketplaceView from './app/marketplace/MarketplaceView';
import Contact from './app/contact/Contact';
import FAQ from './app/faq/Faq';
import FaqAdmin from './app/admin/faq/FaqAdmin';
import Errors from './app/admin/errors/Errors';
import User from './app/user/User';
import MigrateLibraryFiles from '@/tools/MigrateLibraryFiles';
import VerificationAdminPage from './app/admin/verification/page';
import WebsiteAnalytics from './app/admin/analytics/website/WebsiteAnalytics';
import Kanban from './app/admin/kanban/Kanban';

function App() {
  const [currentForm, setCurrentForm] = useState('login');
  const [mfaUserId, setMfaUserId] = useState(null);
  const [mfaEmail, setMfaEmail] = useState(null);
  const [mfaPassword, setMfaPassword] = useState(null);

  const handleSwitchForm = (
    formName,
    userId = null,
    email = null,
    password = null
  ) => {
    if (formName === 'mfa' && userId && email && password) {
      console.log('Switching to MFA for user:', userId, email, password); // ✅ Debug log
      setMfaUserId(userId);
      setMfaEmail(email);
      setMfaPassword(password);
    }
    setCurrentForm(formName);
  };

  const forms = {
    login: <LogInForm onSwitchForm={handleSwitchForm} />,
    register: <RegisterForm onSwitchForm={handleSwitchForm} />,
    forgot: <ForgotForm onSwitchForm={handleSwitchForm} />,
    mfa: (
      <TwoMFA
        onSwitchForm={handleSwitchForm}
        userId={mfaUserId}
        email={mfaEmail}
        password={mfaPassword}
      />
    ),
  };

  return (
    <AuthProvider>
      <Toaster position='top-center' reverseOrder={false} />
      <Router>
        <ThemeReinit />
        <Routes>
          {/* Public Routes */}
          <Route path='/' element={forms[currentForm]} />
          {/* Catch-all route for 404 */}
          <Route path='*' element={<Error404 />} />
          {/* Protected Routes */}
          <Route
            path='/app/dashboard'
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/profile'
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/profile/:uid'
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/dog'
            element={
              <PrivateRoute>
                <Dog />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/library'
            element={
              <PrivateRoute>
                <Library />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/articles'
            element={
              <PrivateRoute>
                <Articles />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/memorial'
            element={
              <PrivateRoute>
                <Memorial />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/directory'
            element={
              <PrivateRoute>
                <Directory />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/calendar'
            element={
              <PrivateRoute>
                <Calendar />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/question'
            element={
              <PrivateRoute>
                <Question />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/marketplace'
            element={
              <PrivateRoute>
                <Marketplace />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/marketplace/:id'
            element={
              <PrivateRoute>
                <MarketplaceView />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/contact'
            element={
              <PrivateRoute>
                <Contact />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/user/:type'
            element={
              <PrivateRoute>
                <User />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/faq'
            element={
              <PrivateRoute>
                <FAQ />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/products'
            element={
              <PrivateRoute>
                <Products />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/adverts'
            element={
              <PrivateRoute>
                <Adverts />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/admin/user/:type'
            element={
              <PrivateRoute adminOnly={true}>
                <UserAdmin />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/admin/library'
            element={
              <PrivateRoute adminOnly={true}>
                <LibraryAdmin />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/admin/articles'
            element={
              <PrivateRoute adminOnly={true}>
                <ArticlesAdmin />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/admin/verification'
            element={
              <PrivateRoute adminOnly={true}>
                <VerificationAdminPage />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/admin/directory'
            element={
              <PrivateRoute adminOnly={true}>
                <DirectoryAdmin />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/admin/marketplace'
            element={
              <PrivateRoute adminOnly={true}>
                <MarketplaceAdmin />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/admin/faq'
            element={
              <PrivateRoute adminOnly={true}>
                <FaqAdmin />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/admin/errors'
            element={
              <PrivateRoute adminOnly={true}>
                <Errors />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/admin/errors'
            element={
              <PrivateRoute adminOnly={true}>
                <Errors />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/admin/website/analytics'
            element={
              <PrivateRoute adminOnly={true}>
                <WebsiteAnalytics />
              </PrivateRoute>
            }
          />
          <Route
            path='/app/admin/kanban'
            element={
              <PrivateRoute adminOnly={true}>
                <Kanban />
              </PrivateRoute>
            }
          />
          {/* REMOVE AFTER NEXT RELEASE Sep 25 */}
          <Route
            path='/app/tools/migrate-library'
            element={
              <PrivateRoute adminOnly={true}>
                <MigrateLibraryFiles />
              </PrivateRoute>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

// Define the PrivateRoute component
function PrivateRoute({ children, adminOnly = false }) {
  const { globalUser, globalData } = useAuth();

  if (!globalUser) {
    // Redirect to login if not authenticated
    return <Navigate to='/' />;
  }

  if (adminOnly && !globalData.isAdmin) {
    // Redirect non-admin users trying to access admin-only routes
    return <Navigate to='/app/dashboard' />;
  }

  // Render the protected component if authenticated
  return children;
}

export default App;
